import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send, Monitor, JapaneseYen } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { MASCOT } from "./brand";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initial = {
    name: "",
    email: "",
    company: "",
    subject: "",
    message: "",
};

export default function Contact() {
    const [form, setForm] = useState(initial);
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    const handle = (e) =>
        setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.subject || !form.message) {
            toast.error("必須項目を入力してください");
            return;
        }
        setLoading(true);
        try {
            await axios.post(`${API}/contact`, form);
            toast.success("お問い合わせを受け付けました。担当者よりご連絡いたします。");
            setDone(true);
            setForm(initial);
        } catch (err) {
            const msg =
                err?.response?.data?.detail ||
                "送信に失敗しました。時間をおいて再度お試しください。";
            toast.error(typeof msg === "string" ? msg : "送信エラー");
        } finally {
            setLoading(false);
        }
    };

    return (
        <section
            id="contact"
            data-testid="contact-section"
            className="relative py-24 sm:py-32 bg-white"
        >
            <div className="max-w-6xl mx-auto px-5 sm:px-8">
                {/* CTA banner */}
                <div className="relative rounded-[2.5rem] bg-gradient-to-br from-[#F0F7E8] via-[#FAFCF5] to-[#FFF6E0] border border-[#E6EFD8] shadow-soft overflow-hidden">
                    <div
                        aria-hidden
                        className="absolute -right-10 -bottom-10 w-60 h-60 rounded-full bg-[#C8E0AC]/40 blur-3xl"
                    />

                    <div className="relative grid lg:grid-cols-12 gap-8 items-center p-8 sm:p-12">
                        <div className="lg:col-span-3 flex justify-center">
                            <img
                                src={MASCOT.cheering}
                                alt="やさまるくん"
                                className="w-44 sm:w-52 h-auto object-contain drop-shadow-[0_12px_24px_rgba(122,167,87,0.18)]"
                                draggable={false}
                            />
                        </div>
                        <div className="lg:col-span-6 text-center lg:text-left">
                            <h2
                                data-testid="contact-title"
                                className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1F2A23] leading-tight"
                            >
                                まずは業務の
                                <br />
                                <span className="text-[#7DB856]">困りごと</span>をお聞かせください
                            </h2>
                            <p className="mt-4 text-sm sm:text-base text-[#5C6B5E]">
                                ご相談・お見積りは無料です。オンラインでのご相談も可能です。
                            </p>
                        </div>
                        <div className="lg:col-span-3 grid grid-cols-2 gap-3">
                            <div className="rounded-2xl bg-white border border-[#E6EFD8] p-4 text-center">
                                <Monitor size={22} strokeWidth={2.5} className="mx-auto text-[#7DB856]" />
                                <div className="mt-1 text-[10px] font-bold text-[#8A9690] tracking-wider">
                                    オンライン相談
                                </div>
                                <div className="text-base font-bold text-[#2F5F1A]">OK</div>
                            </div>
                            <div className="rounded-2xl bg-white border border-[#E6EFD8] p-4 text-center">
                                <JapaneseYen size={22} strokeWidth={2.5} className="mx-auto text-[#7DB856]" />
                                <div className="mt-1 text-[10px] font-bold text-[#8A9690] tracking-wider">
                                    相談・見積り
                                </div>
                                <div className="text-base font-bold text-[#2F5F1A]">無料</div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Form card */}
                <form
                    onSubmit={submit}
                    data-testid="contact-form"
                    className="mt-12 rounded-2xl bg-white border border-[#E6EFD8] shadow-soft p-6 sm:p-10"
                >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <Field label="お名前" required>
                            <Input
                                data-testid="contact-input-name"
                                name="name"
                                value={form.name}
                                onChange={handle}
                                placeholder="やさまる 太郎"
                                className="h-12 rounded-xl border border-[#E6EFD8] focus-visible:ring-2 focus-visible:ring-[#7DB856] focus-visible:border-[#7DB856] bg-[#FAFCF5] text-base font-bold text-[#2F5F1A] placeholder:text-[#A8B5A5] placeholder:font-normal"
                                required
                            />
                        </Field>
                        <Field label="メールアドレス" required>
                            <Input
                                data-testid="contact-input-email"
                                type="email"
                                name="email"
                                value={form.email}
                                onChange={handle}
                                placeholder="taro@example.com"
                                className="h-12 rounded-xl border border-[#E6EFD8] focus-visible:ring-2 focus-visible:ring-[#7DB856] focus-visible:border-[#7DB856] bg-[#FAFCF5] text-base font-bold text-[#2F5F1A] placeholder:text-[#A8B5A5] placeholder:font-normal"
                                required
                            />
                        </Field>
                        <Field label="会社名（任意）">
                            <Input
                                data-testid="contact-input-company"
                                name="company"
                                value={form.company}
                                onChange={handle}
                                placeholder="株式会社やさまる"
                                className="h-12 rounded-xl border border-[#E6EFD8] focus-visible:ring-2 focus-visible:ring-[#7DB856] focus-visible:border-[#7DB856] bg-[#FAFCF5] text-base font-bold text-[#2F5F1A] placeholder:text-[#A8B5A5] placeholder:font-normal"
                            />
                        </Field>
                        <Field label="件名" required>
                            <Input
                                data-testid="contact-input-subject"
                                name="subject"
                                value={form.subject}
                                onChange={handle}
                                placeholder="シフト管理のアプリを作りたい"
                                className="h-12 rounded-xl border border-[#E6EFD8] focus-visible:ring-2 focus-visible:ring-[#7DB856] focus-visible:border-[#7DB856] bg-[#FAFCF5] text-base font-bold text-[#2F5F1A] placeholder:text-[#A8B5A5] placeholder:font-normal"
                                required
                            />
                        </Field>
                    </div>

                    <div className="mt-5">
                        <Field label="お問い合わせ内容" required>
                            <Textarea
                                data-testid="contact-input-message"
                                name="message"
                                value={form.message}
                                onChange={handle}
                                rows={6}
                                placeholder="今エクセルでシフトを管理していて、スマホでも見られるようにしたいです..."
                                className="rounded-xl border border-[#E6EFD8] focus-visible:ring-2 focus-visible:ring-[#7DB856] focus-visible:border-[#7DB856] bg-[#FAFCF5] text-base font-bold text-[#2F5F1A] placeholder:text-[#A8B5A5] placeholder:font-normal min-h-[160px]"
                                required
                            />
                        </Field>
                    </div>

                    <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
                        <p className="text-xs text-[#8A9690] leading-relaxed">
                            送信いただいた内容は、お問い合わせ対応の目的のみに使用します。
                        </p>
                        <button
                            type="submit"
                            data-testid="contact-submit-button"
                            disabled={loading}
                            className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-[#1F2A23] text-white font-semibold tracking-wide shadow-soft hover:bg-[#2F5F1A] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="animate-spin" size={18} strokeWidth={2.75} />
                                    送信中...
                                </>
                            ) : (
                                <>
                                    <Send size={18} strokeWidth={2.75} />
                                    お問い合わせを送信
                                </>
                            )}
                        </button>
                    </div>

                    {done && (
                        <div
                            data-testid="contact-success-message"
                            className="mt-6 rounded-2xl bg-[#E6F2D6] border border-[#C8E0AC] p-4 text-[#2F5F1A] font-bold"
                        >
                            ご連絡ありがとうございます！担当者より2営業日以内にご返信いたします。
                        </div>
                    )}
                </form>
            </div>
        </section>
    );
}

function Field({ label, required, children }) {
    return (
        <label className="block">
            <span className="block text-xs font-extrabold tracking-widest uppercase text-[#5C6B5E] mb-2">
                {label}
                {required && <span className="text-[#C26A8A] ml-1">*</span>}
            </span>
            {children}
        </label>
    );
}
